import { Button, Flex } from "antd";
import { useState } from "react";
import { translate } from "shared/languages";

// Shared by the three "Ada Jadwal yang Terpengaruh" pop-ups (leave, working
// hours, class assignment): the footer that offers a destructive "delete the
// affected classes and proceed" next to the safe proceed-anyway default.
//
// Popup button-color system: solid-ink "Batal" dismiss, a RED destructive
// button (the same convention destructiveConfirm.tsx uses; modalButtonsTheme
// keeps `danger` on the token red), and the navy proceed-anyway confirm as
// the rightmost, safe default. 560 rather than the usual 480: three buttons
// must not wrap.
export const CONFLICT_MODAL_WIDTH = 560;

// Only a server that supports removal sends has_started on every row.
// Against an older server the flag would be silently stripped by its parser
// and the setting saved with NOTHING deleted, so the button is not offered
// until the preview proves the server is new enough.
export const serverSupportsRemoval = (rows: { has_started?: boolean }[]) =>
  rows.length > 0 && rows.every((row) => typeof row.has_started === "boolean");

/**
 * Runs the awaited removal callback with a `removing` flag the modal uses
 * to show the spinner and block every dismiss path. Resolves and lets the
 * caller close on success; on failure stays put (the mutation's own error
 * toast has already explained why), so the admin can still pick the safe
 * proceed or Batal.
 */
export const useRemovalAction = (onConfirmWithRemoval?: () => Promise<void>) => {
  const [removing, setRemoving] = useState(false);
  const runRemoval = async () => {
    if (!onConfirmWithRemoval) return;
    setRemoving(true);
    try {
      await onConfirmWithRemoval();
    } catch {
      // Stay open; see the doc comment above.
    } finally {
      setRemoving(false);
    }
  };
  return { removing, runRemoval };
};

// Modal props that lock the pop-up while a removal is in flight.
export const removalLockProps = (removing: boolean, onCancel: () => void) => ({
  onCancel: removing ? undefined : onCancel,
  closable: !removing,
  maskClosable: !removing,
  width: CONFLICT_MODAL_WIDTH,
});

export const ConflictRemovalFooter = ({
  removing,
  canRemove,
  removeLabel,
  confirmLabel,
  onRemove,
  onConfirm,
  onCancel,
}: {
  removing: boolean;
  canRemove: boolean;
  removeLabel: string;
  confirmLabel: string;
  onRemove: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <Flex justify="flex-end" gap={10} wrap="wrap" style={{ marginTop: 22 }}>
    <Button onClick={onCancel} disabled={removing}>
      {translate("button.cancel")}
    </Button>
    {canRemove && (
      <Button type="primary" danger loading={removing} onClick={onRemove}>
        {removeLabel}
      </Button>
    )}
    <Button type="primary" onClick={onConfirm} disabled={removing}>
      {confirmLabel}
    </Button>
  </Flex>
);
